import { useQuery } from '@tanstack/react-query';
import 'leaflet/dist/leaflet.css';
import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { MapContainer, Marker, Popup, TileLayer } from 'react-leaflet';
import { api, unwrap } from '../../api/client';
import { errorMessage } from '../../api/errors';
import { Badge, Card, Input, PageHeader, Select } from '../../design';
import { fmtRelative } from '../../lib/format';
import {
  type Locker,
  type LockerStatus,
  STATUS,
  STATUS_COLOR,
  StatusBadge,
  TYPE_LABEL,
} from '../lockers/common';
import { OSM_ATTRIBUTION, OSM_URL, pinIcon } from './MiniMap';

/** Harta rețelei (D5): Leaflet + OpenStreetMap, pini după stare, listă laterală filtrabilă. */
export function MapPage() {
  const [q, setQ] = useState('');
  const [status, setStatus] = useState<'' | LockerStatus>('');
  const [selected, setSelected] = useState<string | null>(null);
  const query = useQuery({
    queryKey: ['lockers', 'map'],
    queryFn: () => unwrap(api.GET('/lockers', { params: { query: { pageSize: 100 } } })),
  });
  const lockers = useMemo(() => {
    const items = query.data?.items ?? [];
    const needle = q.trim().toLowerCase();
    return items.filter(
      (l) =>
        (!status || l.status === status) &&
        (!needle ||
          `${l.name} ${l.location.city} ${l.serialNumber}`.toLowerCase().includes(needle)),
    );
  }, [query.data, q, status]);
  const counts = useMemo(() => {
    const c: Partial<Record<LockerStatus, number>> = {};
    for (const l of query.data?.items ?? []) c[l.status] = (c[l.status] ?? 0) + 1;
    return c;
  }, [query.data]);

  return (
    <>
      <PageHeader
        title="Harta rețelei"
        subtitle="Toate lockerele din perimetru, colorate după stare. Tile-uri OpenStreetMap."
      />
      <div
        style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: 16, alignItems: 'start' }}
      >
        <Card tight>
          <MapContainer
            center={[45.9, 24.9]}
            zoom={7}
            style={{ height: 640, width: '100%', borderRadius: 'var(--r)' }}
            scrollWheelZoom
          >
            <TileLayer url={OSM_URL} attribution={OSM_ATTRIBUTION} />
            {lockers.map((l) => (
              <Marker
                key={l.id}
                position={[l.location.lat, l.location.lng]}
                icon={pinIcon(l.status, selected === l.id)}
                eventHandlers={{ click: () => setSelected(l.id) }}
              >
                <Popup>
                  <div style={{ minWidth: 200 }}>
                    <strong>{l.name}</strong>
                    <div className="small muted">
                      {TYPE_LABEL[l.type]} · {l.location.city}
                    </div>
                    <div style={{ margin: '6px 0' }}>
                      <StatusBadge status={l.status} />
                    </div>
                    <div className="small muted">ultimul contact {fmtRelative(l.lastSeenAt)}</div>
                    <Link to={`/lockere/${l.id}`}>Deschide lockerul</Link>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </Card>
        <Card tight>
          <div className="stack" style={{ padding: 16 }}>
            <Input
              placeholder="Caută…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              aria-label="Căutare"
            />
            <Select
              value={status}
              onChange={(e) => setStatus(e.target.value as '' | LockerStatus)}
              aria-label="Stare"
            >
              <option value="">Toate stările</option>
              {(Object.keys(STATUS) as LockerStatus[]).map((st) => (
                <option key={st} value={st}>
                  {STATUS[st].label} ({counts[st] ?? 0})
                </option>
              ))}
            </Select>
            {query.isLoading && <p className="muted small">Se încarcă…</p>}
            {query.error && <p style={{ color: 'var(--red)' }}>{errorMessage(query.error)}</p>}
          </div>
          <ul
            style={{ listStyle: 'none', margin: 0, padding: 0, maxHeight: 480, overflow: 'auto' }}
          >
            {lockers.map((l: Locker) => (
              <li
                key={l.id}
                style={{
                  borderTop: '1px solid var(--line)',
                  padding: '10px 16px',
                  background: selected === l.id ? 'var(--bluelight)' : undefined,
                  cursor: 'pointer',
                }}
                onClick={() => setSelected(l.id)}
              >
                <div className="flex between">
                  <strong>{l.name}</strong>
                  <span
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      background: STATUS_COLOR[l.status],
                      display: 'inline-block',
                    }}
                    aria-label={STATUS[l.status].label}
                  />
                </div>
                <div className="small muted">
                  {l.location.city} · <Link to={`/lockere/${l.id}`}>detalii</Link>
                </div>
              </li>
            ))}
            {!query.isLoading && lockers.length === 0 && (
              <li className="muted small" style={{ padding: 16 }}>
                Niciun locker pentru filtrele alese.
              </li>
            )}
          </ul>
          {query.data && query.data.total > query.data.items.length && (
            <p className="small muted" style={{ padding: 16 }}>
              Harta arată primele 100 de lockere din {query.data.total}.{' '}
              <Badge tone="orange">filtrează în listă</Badge>
            </p>
          )}
        </Card>
      </div>
    </>
  );
}
